import { Stack, Step, StepLabel, Stepper } from '@mui/material'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import PageSection from 'components/common/PageSection'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import {
  getIsDynatraceIntelligentHealthCheckRequest,
  StatusCheckFormStep
} from 'components/status-checks/create/create-status-check-utils'
import { CreateStatusCheckFormActions } from 'components/status-checks/create/CreateStatusCheckFormActions'
import { ObservabilityToolAuthentication } from 'components/status-checks/form/authentication/ObservabilityToolAuthentication'
import { useStatusCheckFormValidationSchemaBuilder } from 'components/status-checks/hooks/use-status-check-form-validation-schema-builder'
import { useStatusCheckRequestBuilder } from 'components/status-checks/hooks/use-status-check-request-builder'
import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  getDefaultStatusCheckFormValues,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { StatusCheckDetailsProvider } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import toast from 'lib/toast'
import { useUpdateService } from 'queries/services/use-update-service'
import { useCreateDynatraceStatusCheck } from 'queries/status-checks/use-create-dynatrace-status-check'
import { useCreateStatusCheck } from 'queries/status-checks/use-create-status-check'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

import type { UserDefinedService } from 'types/api/services/service'

const statusChecksUrls = getStatusChecksUrls()

export const CreateStatusCheckForm: React.FC<{
  service?: UserDefinedService
}> = ({ service }) => {
  const [activeStep, setActiveStep] = useState(
    StatusCheckFormStep.Authentication
  )
  const isAuthenticationFormStep =
    activeStep === StatusCheckFormStep.Authentication

  const navigate = useNavigate()
  const location = useLocation()
  const { referrer = statusChecksUrls.base() } = location.state ?? {}

  const { mutateAsync: createStatusCheck } = useCreateStatusCheck()
  const { mutateAsync: createDynatraceStatusCheck } =
    useCreateDynatraceStatusCheck()
  const { mutateAsync: updateService } = useUpdateService(service?.serviceId)
  const {
    data: existingStatusChecks,
    error: fetchExistingStatusChecksError,
    isError: hasErrorExistingStatusChecks,
    refetch: refetchStatusChecks
  } = useGetStatusChecksAsServiceStatusChecks({
    retryOnMount: false,
    staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
  })

  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    externalIntegrationsError
  } = useStatusCheckExternalIntegrationsContext()
  const externalIntegrations = getAvailableExternalIntegrations(false)

  // Derived query error messages (if any errors have taken place)
  const fetchExistingStatusChecksErrorMessage =
    hasErrorExistingStatusChecks &&
    messageFromResponseError(
      fetchExistingStatusChecksError,
      'An error occurred while fetching existing Health Checks'
    )

  const fetchExternalIntegrationsErrorMessage =
    hasErrorExternalIntegrations &&
    messageFromResponseError(
      externalIntegrationsError,
      'An error occurred while fetching external integrations'
    )

  const buildStatusCheckFormValidationSchema =
    useStatusCheckFormValidationSchemaBuilder(
      externalIntegrations,
      existingStatusChecks ?? []
    )

  const form = useForm<StatusCheckFormValues, StatusCheckFormStep>({
    context: activeStep,
    mode: 'onChange',
    defaultValues: getDefaultStatusCheckFormValues(),
    resolver: buildStatusCheckFormValidationSchema
  })

  const { handleSubmit } = form

  const handleNext = async () => {
    setActiveStep(StatusCheckFormStep.StatusCheck)
  }

  const handleBack = () => {
    setActiveStep(StatusCheckFormStep.Authentication)
    form.reset()
  }

  const buildSaveStatusCheckRequest = useStatusCheckRequestBuilder()
  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const requestPayload = await buildSaveStatusCheckRequest(values)
      const isDynatraceIntelligentHealthCheckRequest =
        getIsDynatraceIntelligentHealthCheckRequest(requestPayload)
      const newStatusCheckId = isDynatraceIntelligentHealthCheckRequest
        ? (await createDynatraceStatusCheck(requestPayload)).identifier
        : await createStatusCheck(requestPayload)

      if (service) {
        await updateService({
          statusCheckIds: [...(service.statusCheckIds ?? []), newStatusCheckId]
        })
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: ms.seconds(2)
      })

      // Refresh status checks list
      await handleDataFetchOnChange(refetchStatusChecks)
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <PageSection
      errorMessage={
        fetchExistingStatusChecksErrorMessage ??
        fetchExternalIntegrationsErrorMessage
      }
      hasError={hasErrorExistingStatusChecks || hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <Stack gap={3}>
        <Stepper
          activeStep={isAuthenticationFormStep ? 0 : 1}
          sx={{ maxWidth: 'sm' }}
        >
          <Step>
            <StepLabel>{StatusCheckFormStep.Authentication}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{StatusCheckFormStep.StatusCheck}</StepLabel>
          </Step>
        </Stepper>
        <FormProvider {...form}>
          <StatusCheckDetailsProvider serviceName={service?.name}>
            <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
              {isAuthenticationFormStep ? (
                <ObservabilityToolAuthentication
                  externalIntegrations={externalIntegrations}
                  onNext={handleNext}
                />
              ) : (
                <Stack spacing={4}>
                  <StatusCheckConnectionForm />
                  <CreateStatusCheckFormActions onCancel={handleBack} />
                </Stack>
              )}
            </form>
          </StatusCheckDetailsProvider>
        </FormProvider>
      </Stack>
    </PageSection>
  )
}
